import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Package, ShoppingBag, Clock, IndianRupee, LogOut, Plus, Trash2, Edit3, Check, X, Upload, Tag, Ticket, MessageCircle, Instagram, Mail, Gift, Archive, RotateCcw, Image as ImageIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ["candles", "figurines", "diy-kits", "floral-bouquets", "home-decor", "garden-decor-accessories", "little-joys-kids"];

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("ave_admin_token") || "");
  const [password, setPassword] = useState("");
  const [logging, setLogging] = useState(false);
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState("false"); // "false" | "true" | "all"
  const [products, setProducts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [insta, setInsta] = useState([]);
  const [landing, setLanding] = useState([]);
  const [homepageTabs, setHomepageTabs] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [stats, setStats] = useState(null);

  const login = async (e) => {
    e.preventDefault();
    setLogging(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem("ave_admin_token", data.token);
      setToken(data.token);
      setPassword("");
      toast.success("Welcome back");
    } catch {
      toast.error("Wrong password");
    }
    setLogging(false);
  };

  const logout = () => {
    localStorage.removeItem("ave_admin_token");
    setToken("");
  };

  const authH = { headers: { Authorization: `Bearer ${token}` } };

  const loadData = useCallback(async () => {
    if (!token) return;
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [ord, prod, st, cp, ig, es, lp, ht] = await Promise.all([
        api.get(`/orders?archived=${orderFilter}`, auth),
        api.get("/products"),
        api.get("/admin/stats", auth),
        api.get("/coupons", auth),
        api.get("/instagram"),
        api.get("/email/status", auth),
        api.get("/landing/all", auth),
        api.get("/homepage-tabs/all", auth),
      ]);
      setOrders(ord.data);
      setProducts(prod.data);
      setStats(st.data);
      setCoupons(cp.data);
      setInsta(ig.data);
      setEmailStatus(es.data);
      setLanding(lp.data);
      setHomepageTabs(ht.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ave_admin_token");
        setToken("");
        toast.error("Session expired");
      }
    }
  }, [token, orderFilter]);

  useEffect(() => {
    if (token) loadData();
  }, [token, orderFilter, loadData]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status }, authH);
      toast.success(`Order marked as ${status}`);
      loadData();
    } catch { toast.error("Could not update status"); }
  };

  const archiveOrder = async (id) => {
    try {
      await api.put(`/orders/${id}/archive`, {}, authH);
      toast.success("Order archived");
      loadData();
    } catch { toast.error("Could not archive"); }
  };

  const restoreOrder = async (id) => {
    try {
      await api.put(`/orders/${id}/restore`, {}, authH);
      toast.success("Order restored");
      loadData();
    } catch { toast.error("Could not restore"); }
  };

  const deleteOrder = async (id, orderNumber) => {
    if (!window.confirm(`Permanently delete order ${orderNumber}? This can't be undone.`)) return;
    try {
      await api.delete(`/orders/${id}`, authH);
      toast.success("Order deleted");
      loadData();
    } catch { toast.error("Could not delete"); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`, authH);
      toast.success("Deleted");
      loadData();
    } catch { toast.error("Could not delete"); }
  };

  const updateProduct = async (id, updates) => {
    try {
      await api.put(`/products/${id}`, updates, authH);
      toast.success("Saved");
      loadData();
    } catch { toast.error("Could not save"); }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-6 py-24" data-testid="admin-login-page">
        <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3">✦ Admin</div>
        <h1 className="font-serif-display text-5xl leading-tight mb-8">Studio access</h1>
        <form onSubmit={login} className="space-y-4">
          <input
            type="password" required
            placeholder="Enter admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60] transition-colors"
            data-testid="admin-password-input"
          />
          <button
            type="submit" disabled={logging}
            className="w-full bg-[#1A1A1A] text-[#FDFBF7] px-6 py-3 rounded-full text-sm uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors disabled:opacity-50"
            data-testid="admin-login-btn"
          >
            {logging ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-2">✦ Admin</div>
          <h1 className="font-serif-display text-5xl leading-tight">Studio dashboard</h1>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-2 text-xs uppercase tracking-widest-plus text-[#9BA595] hover:text-[#D27D60] transition-colors"
          data-testid="admin-logout-btn"
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign out
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Package} label="Products" value={stats.total_products} testid="admin-stat-products" />
          <StatCard icon={ShoppingBag} label="Orders" value={stats.total_orders} testid="admin-stat-orders" />
          <StatCard icon={Clock} label="Pending" value={stats.pending_orders} testid="admin-stat-pending" />
          <StatCard icon={IndianRupee} label="Revenue" value={`₹${stats.revenue}`} testid="admin-stat-revenue" />
        </div>
      )}

      <div className="flex gap-2 border-b border-[#E3DCD2] mb-8 overflow-x-auto">
        <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} testid="admin-tab-orders">Orders</TabBtn>
        <TabBtn active={tab === "products"} onClick={() => setTab("products")} testid="admin-tab-products">Products</TabBtn>
        <TabBtn active={tab === "add"} onClick={() => setTab("add")} testid="admin-tab-add">Add product</TabBtn>
        <TabBtn active={tab === "coupons"} onClick={() => setTab("coupons")} testid="admin-tab-coupons">Coupons</TabBtn>
        <TabBtn active={tab === "instagram"} onClick={() => setTab("instagram")} testid="admin-tab-instagram">Instagram</TabBtn>
        <TabBtn active={tab === "landing"} onClick={() => setTab("landing")} testid="admin-tab-landing">Gift Pages</TabBtn>
        <TabBtn active={tab === "covers"} onClick={() => setTab("covers")} testid="admin-tab-covers">Covers</TabBtn>
        <TabBtn active={tab === "homepage-tabs"} onClick={() => setTab("homepage-tabs")} testid="admin-tab-homepage-tabs">Home Tabs</TabBtn>
      </div>

      {emailStatus && !emailStatus.enabled && tab === "orders" && (
        <div className="mb-6 p-4 rounded-md bg-[#FEF3C7]/60 border border-[#F59E0B]/30 text-xs flex items-center gap-2" data-testid="admin-email-warning">
          <Mail className="w-3.5 h-3.5 text-[#92400E]" strokeWidth={1.5} />
          <span>Email notifications are off. Add <code className="text-[10px] bg-[#FDFBF7] px-1 py-0.5 rounded">RESEND_API_KEY</code> in backend/.env to auto-email customers when you change order status.</span>
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-4">
          <div className="flex items-center gap-2" data-testid="admin-order-filter">
            {[
              { key: "false", label: "Active", tid: "active" },
              { key: "true", label: "Archived", tid: "archived" },
              { key: "all", label: "All", tid: "all" },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setOrderFilter(f.key)}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest-plus transition-colors ${
                  orderFilter === f.key
                    ? "bg-[#1A1A1A] text-[#FDFBF7]"
                    : "border border-[#E3DCD2] hover:border-[#D27D60] hover:text-[#D27D60]"
                }`}
                data-testid={`admin-order-filter-${f.tid}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {orders.length === 0 ? (
            <p className="text-sm text-[#9BA595]">
              {orderFilter === "true" ? "No archived orders." : "No orders yet."}
            </p>
          ) : orders.map((o) => (
            <div key={o.id} className="p-6 rounded-lg border border-[#E3DCD2] bg-[#FDFBF7]" data-testid={`admin-order-${o.order_number}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <div className="font-serif-display text-xl">{o.order_number}</div>
                  <div className="text-xs text-[#9BA595]">{new Date(o.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <NotifyBtn order={o} />
                  <select
                    value={o.status}
                    onChange={(e) => updateStatus(o.id, e.target.value)}
                    className="bg-transparent border border-[#E3DCD2] rounded-full px-3 py-1.5 text-xs uppercase tracking-widest-plus outline-none focus:border-[#D27D60]"
                    data-testid={`admin-order-status-${o.order_number}`}
                  >
                    {["pending","confirmed","shipped","delivered","cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {o.is_archived ? (
                    <>
                      <button
                        onClick={() => restoreOrder(o.id)}
                        title="Restore to active"
                        className="p-1.5 rounded-full hover:text-[#D27D60] transition-colors"
                        data-testid={`admin-order-restore-${o.order_number}`}
                        aria-label="Restore order"
                      >
                        <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => deleteOrder(o.id, o.order_number)}
                        title="Delete forever"
                        className="p-1.5 rounded-full hover:text-[#D27D60] transition-colors"
                        data-testid={`admin-order-delete-${o.order_number}`}
                        aria-label="Delete order"
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => archiveOrder(o.id)}
                      title="Archive this order"
                      className="p-1.5 rounded-full hover:text-[#D27D60] transition-colors"
                      data-testid={`admin-order-archive-${o.order_number}`}
                      aria-label="Archive order"
                    >
                      <Archive className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1">Customer</div>
                  <div>{o.customer_name} · {o.phone}</div>
                  <div className="text-xs text-[#4a4a4a]">{o.email}</div>
                  <div className="text-xs text-[#4a4a4a] mt-1">{o.address}, {o.city}, {o.state} {o.pincode}</div>
                  {o.notes && <div className="text-xs italic text-[#9BA595] mt-1">"{o.notes}"</div>}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1">Items · {o.payment_method.toUpperCase()}{o.coupon_code ? ` · ${o.coupon_code}` : ""}</div>
                  {o.items.map((it, idx) => (
                    <div key={idx}>
                      <div className="text-xs">• {it.name} × {it.quantity} — ₹{it.price * it.quantity}</div>
                      {it.details && it.details.length > 0 && (
                        <ul className="text-[10px] text-[#9BA595] ml-3 space-y-0.5 my-1">
                          {it.details.map((d, di) => <li key={di}>— {d}</li>)}
                        </ul>
                      )}
                    </div>
                  ))}
                  <div className="mt-2 text-xs">Subtotal: ₹{o.subtotal}{o.discount > 0 && ` · Discount: -₹${o.discount}`}</div>
                  <div className="font-medium">Total: ₹{o.total}</div>
                </div>
              </div>
              {o.gift_note && (
                <div className="mt-4 p-4 rounded-md bg-[#D27D60]/10 border border-[#D27D60]/30" data-testid={`admin-order-gift-note-${o.order_number}`}>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest-plus text-[#D27D60] mb-2">
                    <span className="text-base">✎</span> Gift note — print & include
                  </div>
                  <div className="font-serif-display italic text-lg leading-snug text-[#1A1A1A]">"{o.gift_note}"</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "products" && (
        <div className="space-y-3">
          {products.map((p) => (
            <ProductRow key={p.id} product={p} onSave={updateProduct} onDelete={deleteProduct} token={token} onRefresh={loadData} />
          ))}
        </div>
      )}

      {tab === "add" && <AddProductForm token={token} onCreated={loadData} />}

      {tab === "coupons" && <CouponsTab coupons={coupons} authH={authH} onChange={loadData} />}

      {tab === "instagram" && <InstagramTab posts={insta} authH={authH} token={token} onChange={loadData} />}

      {tab === "landing" && <LandingTab pages={landing} products={products} authH={authH} token={token} onChange={loadData} />}

      {tab === "covers" && <CoversTab authH={authH} token={token} />}

      {tab === "homepage-tabs" && <HomepageTabsTab tabs={homepageTabs} authH={authH} token={token} onChange={loadData} />}
    </div>
  );
}

function NotifyBtn({ order }) {
  const cleanPhone = (order.phone || "").replace(/[^0-9]/g, "");
  const digits = cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone;

  const statusLine = {
    pending: `Your order ${order.order_number} has been received. We'll confirm shortly.`,
    confirmed: `Great news! Your order ${order.order_number} is confirmed and being hand-prepared in our Howrah studio.`,
    shipped: `Your order ${order.order_number} has just shipped! You'll receive it soon — carry it home carefully. 🕯️`,
    delivered: `We hope you love your Ave Luxe order ${order.order_number}. Thank you for supporting our little studio!`,
    cancelled: `Your order ${order.order_number} has been cancelled. If this is unexpected, please reply to this message.`,
  }[order.status] || `Update on your order ${order.order_number}: it is now ${order.status}.`;

  const message = encodeURIComponent(
    `Hi ${order.customer_name.split(" ")[0]}!\n\n${statusLine}\n\nTrack anytime: ${window.location.origin}/track-order\n\n— Ave Luxe Candles`
  );

  if (!digits) return null;

  return (
    <a
      href={`https://wa.me/${digits}?text=${message}`}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-1.5 text-xs uppercase tracking-widest-plus px-3 py-1.5 rounded-full border border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white transition-colors"
      data-testid={`admin-notify-${order.order_number}`}
      title={`Notify ${order.customer_name} via WhatsApp`}
    >
      <MessageCircle className="w-3 h-3" strokeWidth={2} />
      Notify
    </a>
  );
}

function ProductRow({ product, onSave, onDelete, token, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [price, setPrice] = useState(product.price);
  const [origPrice, setOrigPrice] = useState(product.original_price || "");
  const [stock, setStock] = useState(product.stock);

  const save = () => {
    const updates = { price: parseFloat(price), stock: parseInt(stock) };
    if (origPrice !== "") updates.original_price = parseFloat(origPrice);
    onSave(product.id, updates);
    setEditing(false);
  };

  return (
    <>
      <div className="p-4 rounded-lg border border-[#E3DCD2] flex items-center gap-4 bg-[#FDFBF7]" data-testid={`admin-product-${product.slug}`}>
        <img src={product.images[0]} alt={product.name} className="w-16 h-16 rounded object-cover" />
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{product.name}</div>
          <div className="text-xs text-[#9BA595]">{product.category}</div>
          {editing ? (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <label className="flex items-center gap-1">
                <span className="text-[#9BA595]">Price ₹</span>
                <input type="number" value={price} onChange={(e) => setPrice(e.target.value)}
                  className="w-20 bg-transparent border border-[#E3DCD2] rounded px-2 py-1 outline-none focus:border-[#D27D60]"
                  data-testid={`admin-price-input-${product.slug}`} />
              </label>
              <label className="flex items-center gap-1">
                <span className="text-[#9BA595]">MRP ₹</span>
                <input type="number" value={origPrice} onChange={(e) => setOrigPrice(e.target.value)}
                  placeholder="—"
                  className="w-20 bg-transparent border border-[#E3DCD2] rounded px-2 py-1 outline-none focus:border-[#D27D60]"
                  data-testid={`admin-orig-price-input-${product.slug}`} />
              </label>
              <label className="flex items-center gap-1">
                <span className="text-[#9BA595]">Stock</span>
                <input type="number" value={stock} onChange={(e) => setStock(e.target.value)}
                  className="w-16 bg-transparent border border-[#E3DCD2] rounded px-2 py-1 outline-none focus:border-[#D27D60]"
                  data-testid={`admin-stock-input-${product.slug}`} />
              </label>
            </div>
          ) : (
            <div className="text-xs text-[#4a4a4a] mt-1">
              ₹{product.price}{product.original_price && ` (was ₹${product.original_price})`} · stock: {product.stock}
            </div>
          )}
        </div>
        {editing ? (
          <>
            <button onClick={save} className="p-2 rounded-full bg-[#D27D60] text-[#FDFBF7] hover:bg-[#c46e51] transition-colors" data-testid={`admin-save-${product.slug}`} aria-label="Save">
              <Check className="w-4 h-4" strokeWidth={2} />
            </button>
            <button onClick={() => { setEditing(false); setPrice(product.price); setStock(product.stock); setOrigPrice(product.original_price || ""); }} className="p-2 hover:text-[#D27D60] transition-colors" data-testid={`admin-cancel-${product.slug}`} aria-label="Cancel">
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </>
        ) : (
          <>
            <button onClick={() => setDetailsOpen(true)} title="Edit full details"
              className="text-[10px] uppercase tracking-widest-plus px-3 py-1.5 rounded-full border border-[#E3DCD2] hover:border-[#D27D60] hover:text-[#D27D60] transition-colors"
              data-testid={`admin-details-${product.slug}`}
            >
              Edit details
            </button>
            <button onClick={() => setEditing(true)} title="Quick price/stock edit"
              className="p-2 hover:text-[#D27D60] transition-colors" data-testid={`admin-edit-${product.slug}`} aria-label="Edit">
              <Edit3 className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button onClick={() => onDelete(product.id)} className="p-2 hover:text-[#D27D60] transition-colors" data-testid={`admin-product-delete-${product.slug}`} aria-label="Delete">
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </>
        )}
      </div>
      {detailsOpen && (
        <ProductDetailsModal
          product={product}
          token={token}
          onClose={() => setDetailsOpen(false)}
          onSaved={() => { setDetailsOpen(false); onRefresh && onRefresh(); }}
        />
      )}
    </>
  );
}

function ProductDetailsModal({ product, token, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: product.name,
    category: product.category,
    short_description: product.short_description,
    description: product.description,
    features: (product.features || []).join(", "),
    tags: (product.tags || []).join(", "),
    weight_g: product.weight_g || 300,
    is_featured: product.is_featured,
    is_new: product.is_new,
  });
  const [images, setImages] = useState(product.images || []);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const BACKEND_URL_LOCAL = process.env.REACT_APP_BACKEND_URL;
  const authH = { headers: { Authorization: `Bearer ${token}` } };

  const set = (k, v) => setForm({ ...form, [k]: v });

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        setImages((prev) => [...prev, `${BACKEND_URL_LOCAL}${data.url}`]);
      } catch (err) {
        toast.error("Upload failed");
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));
  const moveImage = (from, to) => setImages((prev) => {
    if (to < 0 || to >= prev.length) return prev;
    const next = [...prev];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
  });

  const save = async () => {
    if (images.length === 0) return toast.error("Product needs at least one photo");
    setSaving(true);
    try {
      await api.put(`/products/${product.id}`, {
        name: form.name,
        category: form.category,
        short_description: form.short_description,
        description: form.description,
        features: form.features.split(",").map(s => s.trim()).filter(Boolean),
        tags: form.tags.split(",").map(s => s.trim()).filter(Boolean),
        images,
        weight_g: parseInt(form.weight_g) || 300,
        is_featured: form.is_featured,
        is_new: form.is_new,
      }, authH);
      toast.success("Product updated");
      onSaved();
    } catch (err) {
      toast.error("Could not save: " + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid={`admin-details-modal-${product.slug}`}
    >
      <div
        className="bg-[#FDFBF7] rounded-lg max-w-3xl w-full my-10 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-[#E3DCD2]">
          <div>
            <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60]">✦ Edit product</div>
            <div className="font-serif-display text-2xl mt-1">{product.name}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:text-[#D27D60] transition-colors" aria-label="Close" data-testid="admin-details-modal-close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          <div>
            <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-2">Photos ({images.length})</div>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mb-3">
              {images.map((url, idx) => (
                <div key={idx} className="relative aspect-square overflow-hidden rounded-md border border-[#E3DCD2] group">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  {idx === 0 && (
                    <div className="absolute top-1 left-1 text-[9px] uppercase tracking-widest-plus bg-[#D27D60] text-[#FDFBF7] px-1.5 py-0.5 rounded-full">Cover</div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                    <button type="button" onClick={() => moveImage(idx, idx - 1)} disabled={idx === 0} className="w-7 h-7 rounded-full bg-[#FDFBF7] text-[#1A1A1A] flex items-center justify-center hover:text-[#D27D60] disabled:opacity-30" aria-label="Move left">←</button>
                    <button type="button" onClick={() => moveImage(idx, idx + 1)} disabled={idx === images.length - 1} className="w-7 h-7 rounded-full bg-[#FDFBF7] text-[#1A1A1A] flex items-center justify-center hover:text-[#D27D60] disabled:opacity-30" aria-label="Move right">→</button>
                    <button type="button" onClick={() => removeImage(idx)} className="w-7 h-7 rounded-full bg-[#1A1A1A]/80 text-[#FDFBF7] flex items-center justify-center hover:bg-[#D27D60]" aria-label="Remove">
                      <X className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  </div>
                </div>
              ))}
              <label className="aspect-square rounded-md border-2 border-dashed border-[#E3DCD2] hover:border-[#D27D60] transition-colors flex flex-col items-center justify-center cursor-pointer text-xs text-[#9BA595] gap-1">
                <Upload className="w-4 h-4" strokeWidth={1.5} />
                <span>{uploading ? "..." : "Add"}</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid={`admin-details-upload-${product.slug}`} />
              </label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <AdminField label="Name" value={form.name} onChange={(v) => set("name", v)} required />
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1.5">Category</div>
              <select value={form.category} onChange={(e) => set("category", e.target.value)}
                className="w-full bg-transparent border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60]">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>
          <AdminField label="Short description" value={form.short_description} onChange={(v) => set("short_description", v)} />
          <AdminField label="Full description" value={form.description} onChange={(v) => set("description", v)} textarea />
          <AdminField label="Features (comma-separated)" value={form.features} onChange={(v) => set("features", v)} />
          <AdminField label="Tags (comma-separated)" value={form.tags} onChange={(v) => set("tags", v)} />
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_featured} onChange={(e) => set("is_featured", e.target.checked)} />
              Featured / Bestseller
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_new} onChange={(e) => set("is_new", e.target.checked)} />
              New arrival
            </label>
          </div>
        </div>
        <div className="p-6 border-t border-[#E3DCD2] flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-xs uppercase tracking-widest-plus px-5 py-2.5 rounded-full hover:text-[#D27D60] transition-colors">Cancel</button>
          <button onClick={save} disabled={saving}
            className="bg-[#1A1A1A] text-[#FDFBF7] px-6 py-2.5 rounded-full text-xs uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid={`admin-details-save-${product.slug}`}
          >
            <Check className="w-3.5 h-3.5" strokeWidth={2} />
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImageDropzone({ token, uploaded, onUploaded, onRemove }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const uploadFiles = async (files) => {
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        const publicUrl = `${BACKEND_URL}${data.url}`;
        onUploaded(publicUrl);
      } catch (err) {
        toast.error(`Upload failed: ${err.response?.data?.detail || err.message}`);
      }
    }
    setUploading(false);
  };

  return (
    <div className="md:col-span-2">
      <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1.5">Product photos</div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFiles(Array.from(e.dataTransfer.files)); }}
        className={`p-6 rounded-lg border-2 border-dashed text-center transition-colors ${dragging ? "border-[#D27D60] bg-[#F3EFEA]" : "border-[#E3DCD2] hover:border-[#9BA595]"}`}
        data-testid="admin-image-dropzone"
      >
        <input
          ref={inputRef} type="file" accept="image/*" multiple
          className="hidden"
          onChange={(e) => uploadFiles(Array.from(e.target.files))}
          data-testid="admin-image-file-input"
        />
        <Upload className="w-6 h-6 mx-auto text-[#9BA595] mb-2" strokeWidth={1.5} />
        <div className="text-sm mb-1">Drop photos here or</div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="text-xs uppercase tracking-widest-plus underline underline-offset-4 hover:text-[#D27D60] transition-colors"
          data-testid="admin-image-browse-btn"
        >
          {uploading ? "Uploading..." : "Browse files"}
        </button>
        <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mt-2">PNG, JPG · max 8MB each</div>
      </div>

      {uploaded.length > 0 && (
        <div className="mt-4 grid grid-cols-3 md:grid-cols-6 gap-3" data-testid="admin-uploaded-images">
          {uploaded.map((url, idx) => (
            <div key={idx} className="relative aspect-square overflow-hidden rounded-md border border-[#E3DCD2]">
              <img src={url} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => onRemove(idx)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-[#1A1A1A]/80 text-[#FDFBF7] flex items-center justify-center hover:bg-[#D27D60] transition-colors"
                aria-label="Remove"
              >
                <X className="w-3 h-3" strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddProductForm({ token, onCreated }) {
  const [form, setForm] = useState({
    slug: "", name: "", category: "candles", price: "", original_price: "",
    short_description: "", description: "", features: "", tags: "",
    stock: 20, is_featured: false, is_new: true,
  });
  const [images, setImages] = useState([]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (images.length === 0) {
      toast.error("Please upload at least one product photo");
      return;
    }
    try {
      const payload = {
        slug: form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        name: form.name,
        category: form.category,
        price: parseFloat(form.price),
        short_description: form.short_description,
        description: form.description,
        images,
        features: form.features.split(",").map(s => s.trim()).filter(Boolean),
        tags: form.tags.split(",").map(s => s.trim()).filter(Boolean),
        stock: parseInt(form.stock) || 20,
        is_featured: form.is_featured,
        is_new: form.is_new,
      };
      if (form.original_price) payload.original_price = parseFloat(form.original_price);
      await api.post("/products", payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Product added");
      setForm({ slug: "", name: "", category: "candles", price: "", original_price: "", short_description: "", description: "", features: "", tags: "", stock: 20, is_featured: false, is_new: true });
      setImages([]);
      onCreated();
    } catch (err) {
      toast.error("Could not add: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <form onSubmit={submit} className="grid md:grid-cols-2 gap-4 max-w-4xl" data-testid="admin-add-product-form">
      <ImageDropzone
        token={token}
        uploaded={images}
        onUploaded={(url) => setImages((prev) => [...prev, url])}
        onRemove={(idx) => setImages((prev) => prev.filter((_, i) => i !== idx))}
      />
      <AdminField label="Name" value={form.name} onChange={(v) => set("name", v)} required />
      <AdminField label="Slug (optional — auto-generated)" value={form.slug} onChange={(v) => set("slug", v)} />
      <label className="block">
        <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1.5">Category</div>
        <select
          value={form.category}
          onChange={(e) => set("category", e.target.value)}
          className="w-full bg-transparent border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60]"
        >
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>
      <AdminField label="Price (₹)" type="number" value={form.price} onChange={(v) => set("price", v)} required />
      <AdminField label="MRP / original price (optional)" type="number" value={form.original_price} onChange={(v) => set("original_price", v)} />
      <AdminField label="Stock" type="number" value={form.stock} onChange={(v) => set("stock", v)} />
      <AdminField label="Short description" value={form.short_description} onChange={(v) => set("short_description", v)} full />
      <AdminField label="Full description" value={form.description} onChange={(v) => set("description", v)} textarea full />
      <AdminField label="Features (comma-separated)" value={form.features} onChange={(v) => set("features", v)} full />
      <AdminField label="Tags (comma-separated)" value={form.tags} onChange={(v) => set("tags", v)} full />
      <div className="flex items-center gap-6 md:col-span-2">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_featured} onChange={(e) => set("is_featured", e.target.checked)} />
          Featured
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_new} onChange={(e) => set("is_new", e.target.checked)} />
          New arrival
        </label>
      </div>
      <div className="md:col-span-2">
        <button
          type="submit"
          className="bg-[#1A1A1A] text-[#FDFBF7] px-7 py-3.5 rounded-full text-sm uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors flex items-center gap-2"
          data-testid="admin-add-product-btn"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Add product
        </button>
      </div>
    </form>
  );
}

function CouponsTab({ coupons, authH, onChange }) {
  const [form, setForm] = useState({ code: "", discount_type: "percent", discount_value: "", min_order: "", max_uses: "", description: "", active: true });

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/coupons", {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value),
        min_order: form.min_order ? parseFloat(form.min_order) : 0,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        description: form.description || null,
        active: form.active,
      }, authH);
      toast.success("Coupon created");
      setForm({ code: "", discount_type: "percent", discount_value: "", min_order: "", max_uses: "", description: "", active: true });
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    try {
      await api.delete(`/coupons/${id}`, authH);
      toast.success("Deleted");
      onChange();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <form onSubmit={create} className="p-6 rounded-lg bg-[#F3EFEA]/60 space-y-4 h-fit" data-testid="admin-coupon-form">
        <div className="text-xs uppercase tracking-widest-plus text-[#9BA595]">Create coupon</div>
        <AdminField label="Code (e.g., FIRST10)" value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} required />
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1.5">Type</div>
            <select
              value={form.discount_type}
              onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
              className="w-full bg-transparent border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60]"
              data-testid="admin-coupon-type"
            >
              <option value="percent">Percent %</option>
              <option value="flat">Flat ₹</option>
            </select>
          </label>
          <AdminField label={form.discount_type === "percent" ? "Value (%)" : "Value (₹)"} type="number" value={form.discount_value} onChange={(v) => setForm({ ...form, discount_value: v })} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <AdminField label="Min order ₹ (optional)" type="number" value={form.min_order} onChange={(v) => setForm({ ...form, min_order: v })} />
          <AdminField label="Max uses (optional)" type="number" value={form.max_uses} onChange={(v) => setForm({ ...form, max_uses: v })} />
        </div>
        <AdminField label="Description (customer-facing)" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
        <button
          type="submit"
          className="bg-[#1A1A1A] text-[#FDFBF7] px-6 py-3 rounded-full text-xs uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors flex items-center gap-2"
          data-testid="admin-coupon-create-btn"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Create coupon
        </button>
      </form>

      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest-plus text-[#9BA595]">Active coupons ({coupons.length})</div>
        {coupons.length === 0 ? (
          <p className="text-sm text-[#9BA595]">No coupons yet.</p>
        ) : coupons.map((c) => (
          <div key={c.id} className="p-4 rounded-lg border border-[#E3DCD2] bg-[#FDFBF7]" data-testid={`admin-coupon-${c.code}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Ticket className="w-3.5 h-3.5 text-[#D27D60]" strokeWidth={1.5} />
                  <span className="font-serif-display text-xl">{c.code}</span>
                  {!c.active && <span className="text-[10px] uppercase tracking-widest-plus text-[#9BA595]">inactive</span>}
                </div>
                <div className="text-xs text-[#4a4a4a]">
                  {c.discount_type === "percent" ? `${c.discount_value}% off` : `₹${c.discount_value} off`}
                  {c.min_order > 0 && ` · min ₹${c.min_order}`}
                  {c.max_uses && ` · ${c.uses}/${c.max_uses} used`}
                </div>
                {c.description && <div className="text-xs italic text-[#9BA595] mt-1">"{c.description}"</div>}
              </div>
              <button onClick={() => remove(c.id)} className="p-2 hover:text-[#D27D60] transition-colors" data-testid={`admin-coupon-delete-${c.code}`} aria-label="Delete">
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InstagramTab({ posts, authH, token, onChange }) {
  const [form, setForm] = useState({ image_url: "", post_url: "", caption: "", order: 0 });
  const [uploading, setUploading] = useState(false);
  const BACKEND_URL_LOCAL = process.env.REACT_APP_BACKEND_URL;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, image_url: `${BACKEND_URL_LOCAL}${data.url}` }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    }
    setUploading(false);
    e.target.value = "";
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.image_url) {
      toast.error("Upload or paste an image URL first");
      return;
    }
    try {
      await api.post("/instagram", {
        image_url: form.image_url,
        post_url: form.post_url || null,
        caption: form.caption || null,
        order: parseInt(form.order) || 0,
      }, authH);
      toast.success("Instagram post added");
      setForm({ image_url: "", post_url: "", caption: "", order: 0 });
      onChange();
    } catch { toast.error("Could not add"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this Instagram post?")) return;
    try {
      await api.delete(`/instagram/${id}`, authH);
      toast.success("Removed");
      onChange();
    } catch { toast.error("Could not remove"); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <form onSubmit={create} className="p-6 rounded-lg bg-[#F3EFEA]/60 space-y-4 h-fit" data-testid="admin-instagram-form">
        <div className="text-xs uppercase tracking-widest-plus text-[#9BA595]">Add post</div>
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1.5">Photo</div>
          <div className="flex gap-2">
            <input
              type="text" placeholder="Paste image URL or upload"
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              className="flex-1 bg-transparent border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60] transition-colors"
              data-testid="admin-instagram-image-input"
            />
            <label className="px-4 py-3 rounded-md border border-[#E3DCD2] text-xs uppercase tracking-widest-plus cursor-pointer hover:border-[#D27D60] hover:text-[#D27D60] transition-colors flex items-center gap-1.5" data-testid="admin-instagram-upload-btn">
              <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
              {uploading ? "..." : "Upload"}
              <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
            </label>
          </div>
          {form.image_url && <img src={form.image_url} alt="preview" className="mt-3 w-24 h-24 object-cover rounded-md border border-[#E3DCD2]" />}
        </label>
        <AdminField label="Instagram post URL (optional)" value={form.post_url} onChange={(v) => setForm({ ...form, post_url: v })} />
        <AdminField label="Caption (optional)" value={form.caption} onChange={(v) => setForm({ ...form, caption: v })} />
        <AdminField label="Display order (lower = first)" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} />
        <button
          type="submit"
          className="bg-[#1A1A1A] text-[#FDFBF7] px-6 py-3 rounded-full text-xs uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors flex items-center gap-2"
          data-testid="admin-instagram-add-btn"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Add to gallery
        </button>
      </form>

      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest-plus text-[#9BA595] flex items-center gap-2">
          <Instagram className="w-3.5 h-3.5" strokeWidth={1.5} />
          Gallery ({posts.length})
        </div>
        {posts.length === 0 ? (
          <p className="text-sm text-[#9BA595]">Add your first Instagram post — it will appear on the homepage.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {posts.map((p) => (
              <div key={p.id} className="relative aspect-square rounded-md overflow-hidden border border-[#E3DCD2] group" data-testid={`admin-instagram-post-${p.id}`}>
                <img src={p.image_url} alt={p.caption || ""} className="w-full h-full object-cover" />
                <button
                  onClick={() => remove(p.id)}
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-[#1A1A1A]/80 text-[#FDFBF7] flex items-center justify-center hover:bg-[#D27D60] transition-colors opacity-0 group-hover:opacity-100"
                  data-testid={`admin-instagram-delete-${p.id}`}
                  aria-label="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
                {(p.post_url || p.caption) && (
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent text-[#FDFBF7] text-[10px]">
                    {p.caption && <div className="truncate">{p.caption}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CoversTab({ authH, token }) {
  const CATS = [
    { slug: "candles", name: "Scented Candles" },
    { slug: "floral-bouquets", name: "Floral Bouquets" },
    { slug: "figurines", name: "Figurines" },
    { slug: "diy-kits", name: "DIY Kits" },
    { slug: "garden-decor-accessories", name: "Garden Décor" },
  ];
  const [covers, setCovers] = useState({});
  const [uploadingKey, setUploadingKey] = useState(null);
  const BACKEND_URL_LOCAL = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(() => {
    api.get("/category-covers").then(({ data }) => setCovers(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCover = async (slug, url) => {
    try {
      await api.put(`/category-covers/${slug}`, { image_url: url }, authH);
      toast.success("Cover updated");
      load();
    } catch { toast.error("Could not save"); }
  };

  const upload = async (slug, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingKey(slug);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      await saveCover(slug, `${BACKEND_URL_LOCAL}${data.url}`);
    } catch { toast.error("Upload failed"); }
    setUploadingKey(null);
    e.target.value = "";
  };

  return (
    <div data-testid="admin-covers-tab">
      <div className="text-xs uppercase tracking-widest-plus text-[#9BA595] mb-6">
        Homepage "Shop by category" cover images
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {CATS.map((c) => (
          <div key={c.slug} className="rounded-lg overflow-hidden border border-[#E3DCD2] bg-[#FDFBF7]" data-testid={`admin-cover-${c.slug}`}>
            <div className="aspect-video bg-[#F3EFEA] relative">
              {covers[c.slug] ? (
                <img src={covers[c.slug]} alt={c.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#9BA595] text-xs uppercase tracking-widest-plus gap-2">
                  <ImageIcon className="w-4 h-4" strokeWidth={1.5} /> Using default
                </div>
              )}
            </div>
            <div className="p-4 space-y-3">
              <div className="font-medium">{c.name}</div>
              <label className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full border border-[#E3DCD2] hover:border-[#D27D60] hover:text-[#D27D60] transition-colors cursor-pointer text-xs uppercase tracking-widest-plus">
                <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                {uploadingKey === c.slug ? "Uploading..." : covers[c.slug] ? "Replace photo" : "Upload photo"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(c.slug, e)} data-testid={`admin-cover-upload-${c.slug}`} />
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LandingTab({ pages, products, authH, token, onChange }) {
  const [form, setForm] = useState({
    slug: "", title: "", subtitle: "", hero_image: "", theme_color: "#D27D60",
    intro: "", product_slugs: [], cta_label: "Shop the collection", cta_url: "/shop", active: true,
  });
  const [uploading, setUploading] = useState(false);
  const BACKEND_URL_LOCAL = process.env.REACT_APP_BACKEND_URL;

  const handleHeroFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, hero_image: `${BACKEND_URL_LOCAL}${data.url}` }));
      toast.success("Hero image uploaded");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
    e.target.value = "";
  };

  const toggleProduct = (slug) => {
    setForm((prev) => ({
      ...prev,
      product_slugs: prev.product_slugs.includes(slug)
        ? prev.product_slugs.filter((s) => s !== slug)
        : [...prev.product_slugs, slug],
    }));
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.slug || !form.title) return toast.error("Slug and title are required");
    if (form.product_slugs.length === 0) return toast.error("Pick at least one product");
    try {
      await api.post("/landing", form, authH);
      toast.success("Gift page created");
      setForm({ slug: "", title: "", subtitle: "", hero_image: "", theme_color: "#D27D60", intro: "", product_slugs: [], cta_label: "Shop the collection", cta_url: "/shop", active: true });
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this gift page?")) return;
    try {
      await api.delete(`/landing/${id}`, authH);
      toast.success("Deleted");
      onChange();
    } catch { toast.error("Could not delete"); }
  };

  const publicUrl = (slug) => `${window.location.origin}/gifts/${slug}`;

  const copyLink = (slug) => {
    navigator.clipboard.writeText(publicUrl(slug)).then(() => toast.success("Link copied — paste it on WhatsApp!"));
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={create} className="p-6 rounded-lg bg-[#F3EFEA]/60 space-y-4 h-fit" data-testid="admin-landing-form">
        <div className="text-xs uppercase tracking-widest-plus text-[#9BA595]">Create gift page</div>
        <div className="grid grid-cols-2 gap-3">
          <AdminField label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
          <AdminField label="Slug (e.g., valentines)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} required />
        </div>
        <AdminField label="Subtitle (small text above title)" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} />
        <AdminField label="Intro paragraph" value={form.intro} onChange={(v) => setForm({ ...form, intro: v })} textarea />
        <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
          <AdminField label="Theme color (hex)" value={form.theme_color} onChange={(v) => setForm({ ...form, theme_color: v })} />
          <div className="w-12 h-12 rounded-full border border-[#E3DCD2]" style={{ backgroundColor: form.theme_color }} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1.5">Hero image (optional)</div>
          <div className="flex gap-2">
            <input
              type="text" placeholder="Paste image URL or upload"
              value={form.hero_image}
              onChange={(e) => setForm({ ...form, hero_image: e.target.value })}
              className="flex-1 bg-transparent border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60] transition-colors"
              data-testid="admin-landing-hero-input"
            />
            <label className="px-4 py-3 rounded-md border border-[#E3DCD2] text-xs uppercase tracking-widest-plus cursor-pointer hover:border-[#D27D60] hover:text-[#D27D60] transition-colors flex items-center gap-1.5">
              <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
              {uploading ? "..." : "Upload"}
              <input type="file" accept="image/*" onChange={handleHeroFile} className="hidden" />
            </label>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-2">
            Pick products ({form.product_slugs.length} selected)
          </div>
          <div className="max-h-64 overflow-y-auto border border-[#E3DCD2] rounded-md p-2 grid grid-cols-2 gap-1.5 bg-[#FDFBF7]" data-testid="admin-landing-product-picker">
            {products.map((p) => {
              const picked = form.product_slugs.includes(p.slug);
              return (
                <label
                  key={p.id}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${picked ? "bg-[#D27D60]/10 border border-[#D27D60]/30" : "hover:bg-[#F3EFEA]"}`}
                  data-testid={`admin-landing-pick-${p.slug}`}
                >
                  <input type="checkbox" checked={picked} onChange={() => toggleProduct(p.slug)} className="shrink-0" />
                  <img src={p.images[0]} alt={p.name} className="w-8 h-8 rounded object-cover" />
                  <span className="text-xs truncate flex-1">{p.name}</span>
                </label>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <AdminField label="CTA label" value={form.cta_label} onChange={(v) => setForm({ ...form, cta_label: v })} />
          <AdminField label="CTA link (e.g., /shop/candles)" value={form.cta_url} onChange={(v) => setForm({ ...form, cta_url: v })} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          Active (visible to customers)
        </label>
        <button
          type="submit"
          className="bg-[#1A1A1A] text-[#FDFBF7] px-6 py-3 rounded-full text-xs uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors flex items-center gap-2"
          data-testid="admin-landing-create-btn"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Create gift page
        </button>
      </form>

      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest-plus text-[#9BA595] flex items-center gap-2">
          <Gift className="w-3.5 h-3.5" strokeWidth={1.5} /> Gift pages ({pages.length})
        </div>
        {pages.length === 0 ? (
          <p className="text-sm text-[#9BA595]">Create your first seasonal gift page — Valentines, Diwali, Mother's Day…</p>
        ) : pages.map((p) => (
          <div key={p.id} className="p-4 rounded-lg border border-[#E3DCD2] bg-[#FDFBF7]" data-testid={`admin-landing-${p.slug}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.theme_color }} />
                  <span className="font-serif-display text-xl">{p.title}</span>
                  {!p.active && <span className="text-[10px] uppercase tracking-widest-plus text-[#9BA595]">draft</span>}
                </div>
                <div className="text-xs text-[#4a4a4a]">/gifts/{p.slug} · {p.product_slugs.length} products</div>
                {p.subtitle && <div className="text-xs italic text-[#9BA595] mt-1">"{p.subtitle}"</div>}
                <div className="mt-3 flex flex-wrap gap-2">
                  <a
                    href={`/gifts/${p.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] uppercase tracking-widest-plus px-3 py-1.5 rounded-full border border-[#E3DCD2] hover:border-[#D27D60] hover:text-[#D27D60] transition-colors"
                    data-testid={`admin-landing-preview-${p.slug}`}
                  >
                    Preview
                  </a>
                  <button
                    onClick={() => copyLink(p.slug)}
                    className="text-[10px] uppercase tracking-widest-plus px-3 py-1.5 rounded-full border border-[#E3DCD2] hover:border-[#D27D60] hover:text-[#D27D60] transition-colors"
                    data-testid={`admin-landing-copy-${p.slug}`}
                  >
                    Copy link
                  </button>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`${p.title} — ${publicUrl(p.slug)}`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] uppercase tracking-widest-plus px-3 py-1.5 rounded-full border border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white transition-colors"
                    data-testid={`admin-landing-whatsapp-${p.slug}`}
                  >
                    Share on WhatsApp
                  </a>
                </div>
              </div>
              <button onClick={() => remove(p.id)} className="p-2 hover:text-[#D27D60] transition-colors" data-testid={`admin-landing-delete-${p.slug}`} aria-label="Delete">
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HomepageTabsTab({ tabs, authH, token, onChange }) {
  const emptyForm = { title: "", subtitle: "", body: "", image_url: "", link_label: "", link_url: "", order: 0, active: true };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const BACKEND_URL_LOCAL = process.env.REACT_APP_BACKEND_URL;

  const resetForm = () => { setForm(emptyForm); setEditingId(null); };

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({
      title: t.title || "",
      subtitle: t.subtitle || "",
      body: t.body || "",
      image_url: t.image_url || "",
      link_label: t.link_label || "",
      link_url: t.link_url || "",
      order: t.order ?? 0,
      active: t.active !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, image_url: `${BACKEND_URL_LOCAL}${data.url}` }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    const payload = {
      title: form.title.trim(),
      subtitle: form.subtitle || null,
      body: form.body || null,
      image_url: form.image_url || null,
      link_label: form.link_label || null,
      link_url: form.link_url || null,
      order: parseInt(form.order) || 0,
      active: form.active,
    };
    try {
      if (editingId) {
        await api.put(`/homepage-tabs/${editingId}`, payload, authH);
        toast.success("Tab updated");
      } else {
        await api.post("/homepage-tabs", payload, authH);
        toast.success("Tab created");
      }
      resetForm();
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this tab? It will be removed from the homepage.")) return;
    try {
      await api.delete(`/homepage-tabs/${id}`, authH);
      toast.success("Tab removed");
      if (editingId === id) resetForm();
      onChange();
    } catch { toast.error("Could not delete"); }
  };

  const toggleActive = async (t) => {
    try {
      await api.put(`/homepage-tabs/${t.id}`, { active: !t.active }, authH);
      onChange();
    } catch { toast.error("Could not update"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="admin-homepage-tabs">
      <form onSubmit={submit} className="p-6 rounded-lg bg-[#F3EFEA]/60 space-y-4 h-fit" data-testid="admin-homepage-tab-form">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest-plus text-[#9BA595]">
            {editingId ? "Edit tab" : "Add homepage tab"}
          </div>
          {editingId && (
            <button type="button" onClick={resetForm} className="text-[10px] uppercase tracking-widest-plus underline underline-offset-4 hover:text-[#D27D60]" data-testid="admin-homepage-tab-cancel-edit">
              Cancel edit
            </button>
          )}
        </div>
        <AdminField label="Tab title (e.g., Little Joys for kids)" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
        <AdminField label="Small label above title (optional)" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} />
        <AdminField label="Body text" value={form.body} onChange={(v) => setForm({ ...form, body: v })} textarea />
        <div>
          <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1.5">Image (optional)</div>
          <div className="flex gap-2">
            <input
              type="text" placeholder="Paste image URL or upload"
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              className="flex-1 bg-transparent border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60] transition-colors"
              data-testid="admin-homepage-tab-image-input"
            />
            <label className="px-4 py-3 rounded-md border border-[#E3DCD2] text-xs uppercase tracking-widest-plus cursor-pointer hover:border-[#D27D60] hover:text-[#D27D60] transition-colors flex items-center gap-1.5">
              <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
              {uploading ? "..." : "Upload"}
              <input type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="admin-homepage-tab-image-file" />
            </label>
          </div>
          {form.image_url && <img src={form.image_url} alt="preview" className="mt-3 w-24 h-24 object-cover rounded-md border border-[#E3DCD2]" />}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <AdminField label="CTA label (optional)" value={form.link_label} onChange={(v) => setForm({ ...form, link_label: v })} />
          <AdminField label="CTA link (e.g., /shop/diy-kits)" value={form.link_url} onChange={(v) => setForm({ ...form, link_url: v })} />
        </div>
        <div className="grid grid-cols-2 gap-3 items-end">
          <AdminField label="Order (lower = first)" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} />
          <label className="flex items-center gap-2 text-sm pb-2">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="admin-homepage-tab-active" />
            Visible on homepage
          </label>
        </div>
        <button
          type="submit"
          className="bg-[#1A1A1A] text-[#FDFBF7] px-6 py-3 rounded-full text-xs uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors flex items-center gap-2"
          data-testid="admin-homepage-tab-save-btn"
        >
          {editingId ? <><Check className="w-3.5 h-3.5" strokeWidth={2} /> Save changes</> : <><Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Add tab</>}
        </button>
      </form>

      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest-plus text-[#9BA595]">Homepage tabs ({tabs.length})</div>
        {tabs.length === 0 ? (
          <p className="text-sm text-[#9BA595]">No tabs yet — add your first one to feature content on the homepage.</p>
        ) : tabs.map((t) => (
          <div key={t.id} className="p-4 rounded-lg border border-[#E3DCD2] bg-[#FDFBF7]" data-testid={`admin-homepage-tab-${t.id}`}>
            <div className="flex items-start gap-3">
              {t.image_url && (
                <img src={t.image_url} alt={t.title} className="w-16 h-16 rounded-md object-cover shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-serif-display text-xl">{t.title}</span>
                  {!t.active && <span className="text-[10px] uppercase tracking-widest-plus text-[#9BA595]">hidden</span>}
                  <span className="text-[10px] uppercase tracking-widest-plus text-[#9BA595]">order {t.order}</span>
                </div>
                {t.subtitle && <div className="text-xs italic text-[#9BA595]">"{t.subtitle}"</div>}
                {t.body && <div className="text-xs text-[#4a4a4a] mt-1 line-clamp-2">{t.body}</div>}
                {t.link_url && <div className="text-[10px] uppercase tracking-widest-plus text-[#D27D60] mt-1.5">→ {t.link_label || "CTA"} · {t.link_url}</div>}
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <button onClick={() => startEdit(t)} className="p-1.5 hover:text-[#D27D60] transition-colors" data-testid={`admin-homepage-tab-edit-${t.id}`} aria-label="Edit">
                  <Edit3 className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button onClick={() => toggleActive(t)} className="p-1.5 hover:text-[#D27D60] transition-colors" data-testid={`admin-homepage-tab-toggle-${t.id}`} aria-label={t.active ? "Hide" : "Show"} title={t.active ? "Hide from homepage" : "Show on homepage"}>
                  {t.active ? <X className="w-4 h-4" strokeWidth={1.5} /> : <Check className="w-4 h-4" strokeWidth={1.5} />}
                </button>
                <button onClick={() => remove(t.id)} className="p-1.5 hover:text-[#D27D60] transition-colors" data-testid={`admin-homepage-tab-delete-${t.id}`} aria-label="Delete">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="p-6 rounded-lg bg-[#F3EFEA]/60 border border-[#E3DCD2]" data-testid={testid}>
      <Icon className="w-4 h-4 text-[#D27D60] mb-3" strokeWidth={1.5} />
      <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1">{label}</div>
      <div className="font-serif-display text-3xl">{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-3 text-xs uppercase tracking-widest-plus transition-colors border-b-2 whitespace-nowrap ${active ? "border-[#D27D60] text-[#D27D60]" : "border-transparent hover:text-[#D27D60]"}`}
      data-testid={testid}
    >{children}</button>
  );
}

function AdminField({ label, value, onChange, type = "text", required, textarea, full }) {
  const cls = "w-full bg-transparent border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60] transition-colors";
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1.5">{label}{required && " *"}</div>
      {textarea ? (
        <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} className={`${cls} resize-none`} />
      ) : (
        <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
      )}
    </label>
  );
}
